import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Wallet,
  Calendar,
  DollarSign,
  Search,
  Send,
  CheckCircle2,
  CircleDollarSign,
  Ban,
  X,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useLocaleFormat } from "@/lib/i18n-format";
import { useScopedCompanyId } from "@/owner/useScopedCompanyId";
import { useOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

import { RequirePremium } from "@/components/subscription/RequirePremium";

export const Route = createFileRoute("/_app/payroll/")({
  component: PayrollIndexRouteComponent,
});

function PayrollIndexRouteComponent() {
  return (
    <RequirePremium featureKey="nav.payroll">
      <PayrollIndex />
    </RequirePremium>
  );
}

type RunStatus = "draft" | "pending_employee_approval" | "finalized" | "paid" | "cancelled";

type Run = {
  id: string;
  number: string;
  name: string | null;
  period_start: string;
  period_end: string;
  pay_date: string | null;
  status: RunStatus;
  currency: string;
  total_hours: number;
  total_gross: number;
  created_at: string;
  company_id: string;
};

type PeriodFilter = string; // "all" ou a chave "<period_start>|<period_end>"
type StatusFilter = "all" | RunStatus;
type ProjectFilter = string; // "all", um project_id, ou "__noproject__"

const NO_PROJECT = "__noproject__";

function PayrollIndex() {
  const { t } = useTranslation();
  const { company, hasRole } = useAuth();
  const { formatDate } = useLocaleFormat();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");
  const scopedCompanyId = useScopedCompanyId();
  const { isOwner } = useOwnerCompany();
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>("all");
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>("all");
  // Obras (projetos) de cada folha: runId -> Set<project_id | "__noproject__">.
  // Uma folha agrega horas de várias obras; a info fica em payroll_run_entry_links.
  const [runProjects, setRunProjects] = useState<Record<string, Set<string>>>({});
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});

  const load = async () => {
    if (!isOwner && !company?.id) return;
    setLoading(true);
    let q = supabase.from("payroll_runs").select("*").order("period_end", { ascending: false });
    if (scopedCompanyId) q = q.eq("company_id", scopedCompanyId);
    const { data, error } = await q;
    if (error) toast.error(error.message);
    const list = (data ?? []) as Run[];
    setRuns(list);

    // Map each run to the obras (projects) it touches, via the entry links.
    const runIds = list.map((r) => r.id);
    if (runIds.length > 0) {
      const { data: links } = await supabase
        .from("payroll_run_entry_links")
        .select("payroll_run_id, project_id, project:projects(name)")
        .in("payroll_run_id", runIds);
      type ProjectRel = { name: string | null } | { name: string | null }[] | null;
      type LinkRow = {
        payroll_run_id: string;
        project_id: string | null;
        project: ProjectRel;
      };
      const rp: Record<string, Set<string>> = {};
      const pn: Record<string, string> = {};
      ((links ?? []) as unknown as LinkRow[]).forEach((l) => {
        const rid = l.payroll_run_id;
        const pid = l.project_id ?? NO_PROJECT;
        if (!rp[rid]) rp[rid] = new Set();
        rp[rid].add(pid);
        const name = Array.isArray(l.project) ? l.project[0]?.name : l.project?.name;
        if (pid !== NO_PROJECT && name) pn[pid] = name;
      });
      setRunProjects(rp);
      setProjectNames(pn);
    } else {
      setRunProjects({});
      setProjectNames({});
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedCompanyId, isOwner]);

  const fmtMoney = (v: number, cur: string) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(v);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return runs.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (periodFilter !== "all" && `${r.period_start}|${r.period_end}` !== periodFilter)
        return false;
      if (projectFilter !== "all") {
        const set = runProjects[r.id];
        if (!set || !set.has(projectFilter)) return false;
      }
      if (q) {
        const hay = `${r.number} ${r.name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [runs, search, statusFilter, periodFilter, projectFilter, runProjects]);

  // Períodos de pagamento distintos presentes nas folhas (pro filtro por período).
  const distinctPeriods = useMemo(() => {
    const map = new Map<string, { key: string; start: string; end: string }>();
    for (const r of runs) {
      const key = `${r.period_start}|${r.period_end}`;
      if (!map.has(key)) map.set(key, { key, start: r.period_start, end: r.period_end });
    }
    return Array.from(map.values()).sort((a, b) => (a.end < b.end ? 1 : -1));
  }, [runs]);

  // Obras (projetos) distintas presentes nas folhas (pro filtro por obra).
  const distinctProjects = useMemo(() => {
    const ids = new Set<string>();
    for (const set of Object.values(runProjects)) for (const id of set) ids.add(id);
    let hasNoProject = false;
    const named = Array.from(ids)
      .filter((id) => {
        if (id === NO_PROJECT) {
          hasNoProject = true;
          return false;
        }
        return true;
      })
      .map((id) => ({ id, name: projectNames[id] ?? t("pages.payroll.noProject") }))
      .sort((a, b) => a.name.localeCompare(b.name));
    // "Sem obra" sempre por último.
    return hasNoProject
      ? [...named, { id: NO_PROJECT, name: t("pages.payroll.noProject") }]
      : named;
  }, [runProjects, projectNames, t]);

  const totalGross = filtered.reduce((s, r) => s + Number(r.total_gross || 0), 0);
  const totalHours = filtered.reduce((s, r) => s + Number(r.total_hours || 0), 0);

  const hasActiveFilters =
    search !== "" || statusFilter !== "all" || periodFilter !== "all" || projectFilter !== "all";

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setPeriodFilter("all");
    setProjectFilter("all");
  };

  const quickAction = async (
    e: React.MouseEvent,
    runId: string,
    nextStatus: RunStatus,
    successKey: "sent" | "finalized" | "paid" | "cancelled",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const run = runs.find((r) => r.id === runId);
    if (!run) return;
    const patch: Partial<Run> = { status: nextStatus };
    if (nextStatus === "finalized") {
      // finalized_at is set on detail page too; harmless to omit here
    }
    if (nextStatus === "paid" && !run.pay_date) {
      patch.pay_date = new Date().toISOString().slice(0, 10);
    }
    const { error } = await supabase.from("payroll_runs").update(patch).eq("id", runId);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t(`pages.payroll.actions.${successKey}`));
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("pages.payroll.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("pages.payroll.subtitle")}</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/payroll/tax-forms" })}
              className="gap-2"
            >
              <FileText className="h-4 w-4" /> {t("pages.taxForms.navLink")}
            </Button>
            <Button onClick={() => navigate({ to: "/payroll/new" })} className="gap-2">
              <Plus className="h-4 w-4" /> {t("pages.payroll.new")}
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">{t("pages.payroll.totalRuns")}</p>
              <p className="mt-1 text-2xl font-bold">{filtered.length}</p>
            </div>
            <Wallet className="h-8 w-8 text-primary/60" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">{t("pages.payroll.totalHours")}</p>
              <p className="mt-1 text-2xl font-bold">{totalHours.toFixed(2)}</p>
            </div>
            <Calendar className="h-8 w-8 text-primary/60" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">{t("pages.payroll.totalGross")}</p>
              <p className="mt-1 text-2xl font-bold">
                {fmtMoney(totalGross, company?.currency ?? "USD")}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-primary/60" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="text-lg">{t("pages.payroll.allRuns")}</CardTitle>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("pages.payroll.searchPlaceholder")}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <SelectTrigger className="sm:w-[200px]">
                <SelectValue placeholder={t("pages.payroll.filterByStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pages.payroll.allStatuses")}</SelectItem>
                <SelectItem value="draft">{t("statuses.draft")}</SelectItem>
                <SelectItem value="finalized">{t("statuses.finalized")}</SelectItem>
                <SelectItem value="paid">{t("statuses.paid")}</SelectItem>
                <SelectItem value="cancelled">{t("statuses.cancelled")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={periodFilter} onValueChange={(v) => setPeriodFilter(v as PeriodFilter)}>
              <SelectTrigger className="sm:w-[230px]">
                <SelectValue placeholder={t("pages.payroll.filterByPeriod")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pages.payroll.allPeriods")}</SelectItem>
                {distinctPeriods.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {formatDate(p.start)} – {formatDate(p.end)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {distinctProjects.length > 0 && (
              <Select
                value={projectFilter}
                onValueChange={(v) => setProjectFilter(v as ProjectFilter)}
              >
                <SelectTrigger className="sm:w-[220px]">
                  <SelectValue placeholder={t("pages.payroll.filterByProject")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.payroll.allProjects")}</SelectItem>
                  {distinctProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="gap-1" onClick={clearFilters}>
                <X className="h-4 w-4" /> {t("pages.payroll.clearFilters")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : runs.length === 0 ? (
            <div className="py-12 text-center">
              <Wallet className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">{t("pages.payroll.empty")}</p>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="mt-4 gap-2"
                  onClick={() => navigate({ to: "/payroll/new" })}
                >
                  <Plus className="h-4 w-4" /> {t("pages.payroll.createFirst")}
                </Button>
              )}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">
                {t("pages.payroll.noResultsFiltered")}
              </p>
              <Button variant="outline" size="sm" className="mt-3 gap-1" onClick={clearFilters}>
                <X className="h-4 w-4" /> {t("pages.payroll.clearFilters")}
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((r) => {
                return (
                  <li key={r.id}>
                    <Link
                      to="/payroll/$runId"
                      params={{ runId: r.id }}
                      className="flex flex-col gap-3 px-2 py-3 transition-colors hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-semibold">{r.number}</span>
                          <CompanyBadge companyId={r.company_id} />
                          <StatusBadge status={r.status} label={t(`statuses.${r.status}`)} />
                        </div>
                        <p className="mt-0.5 truncate text-sm">
                          {r.name ?? t("pages.payroll.payPeriod")} · {formatDate(r.period_start)} –{" "}
                          {formatDate(r.period_end)}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                        <div className="text-right">
                          <p className="text-sm font-semibold">
                            {fmtMoney(Number(r.total_gross), r.currency)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {Number(r.total_hours).toFixed(2)} h
                          </p>
                        </div>
                        {isAdmin && (
                          <div className="flex flex-wrap gap-1">
                            {r.status === "draft" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1"
                                onClick={(e) => quickAction(e, r.id, "finalized", "finalized")}
                                title={t("pages.payroll.actions.approveTooltip")}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {r.status === "pending_employee_approval" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1"
                                onClick={(e) => quickAction(e, r.id, "finalized", "finalized")}
                                title={t("pages.payroll.actions.finalizeTooltip")}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {r.status === "finalized" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1"
                                onClick={(e) => quickAction(e, r.id, "paid", "paid")}
                                title={t("pages.payroll.actions.markPaid")}
                              >
                                <CircleDollarSign className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {r.status !== "paid" && r.status !== "cancelled" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1 text-muted-foreground"
                                onClick={(e) => quickAction(e, r.id, "cancelled", "cancelled")}
                                title={t("pages.payroll.actions.cancel")}
                              >
                                <Ban className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status, label }: { status: RunStatus; label: string }) {
  const map: Record<RunStatus, string> = {
    draft: "border-muted-foreground/40 text-muted-foreground",
    pending_employee_approval: "border-primary/40 text-primary",
    finalized: "border-primary/40 text-primary",
    paid: "border-primary/40 text-primary bg-primary/5",
    cancelled: "border-destructive/40 text-destructive",
  };
  return (
    <Badge variant="outline" className={map[status]}>
      {label}
    </Badge>
  );
}
